import {
  Amenity,
  MealPlanKind,
  type RoomInfoProducts,
} from "@b2bportal/lodging-api";

const BREAKFAST_AMENITIES = new Set<string>([
  Amenity.FreeBreakfast,
  Amenity.BreakfastIncluded,
]);

const BREAKFAST_MEAL_PLAN_KINDS = new Set<string>([
  MealPlanKind.Breakfast,
  MealPlanKind.BreakfastFor1,
  MealPlanKind.BreakfastFor2,
]);

const MEAL_PLAN_I18N_KEYS: Record<string, string> = {
  [MealPlanKind.Breakfast]: "core-hotels.mealPlan.Breakfast",
  [MealPlanKind.BreakfastFor1]: "core-hotels.mealPlan.BreakfastFor1",
  [MealPlanKind.BreakfastFor2]: "core-hotels.mealPlan.BreakfastFor2",
  [MealPlanKind.HalfBoard]: "core-hotels.mealPlan.HalfBoard",
  [MealPlanKind.FullBoard]: "core-hotels.mealPlan.FullBoard",
  [MealPlanKind.AllInclusive]: "core-hotels.mealPlan.AllInclusive",
  [MealPlanKind.Lunch]: "core-hotels.mealPlan.Lunch",
  [MealPlanKind.Dinner]: "core-hotels.mealPlan.Dinner",
};

export type MealPlanDisplay = { label: string; isBreakfast: boolean };

/**
 * Determines the meal plan badge to display on a room card.
 *
 * Room-rate data is the source of truth for whether breakfast is included
 * in the booking (NU-147). The function checks, in order:
 *   1. All rates share the same non-RoomOnly meal plan → show that plan name
 *   2. Room-level amenities include breakfast → show "free breakfast"
 *   3. All rates include breakfast via rate amenities/meal plan → show "free breakfast"
 *   4. Otherwise → no badge (undefined)
 */
export function getRoomMealPlanDisplay(
  room: RoomInfoProducts,
  t: (key: string) => string
): MealPlanDisplay | undefined {
  const mealPlanKinds = room.products.map(
    (p) => p.mealPlan?.kind ?? MealPlanKind.RoomOnly
  );
  const uniqueKinds = Array.from(new Set(mealPlanKinds));

  // All rates share the same non-RoomOnly meal plan → show that meal plan name.
  // Room-rate data is the source of truth for whether breakfast is included
  // in the booking (NU-147).
  if (uniqueKinds.length === 1 && uniqueKinds[0] !== MealPlanKind.RoomOnly) {
    const kind = uniqueKinds[0];
    const isBreakfast = BREAKFAST_MEAL_PLAN_KINDS.has(kind);
    const key = MEAL_PLAN_I18N_KEYS[kind];
    if (key == null) return undefined;
    return { label: t(key), isBreakfast };
  }

  const freeBreakfastLabel = t(
    "core-hotels.availabilityPage.results.hotelCard.features.breakfastFree"
  );

  // Room-level amenities (static metadata from the provider).
  if (
    room.roomInfo.amenities?.some((a) => BREAKFAST_AMENITIES.has(a.amenity))
  ) {
    return { label: freeBreakfastLabel, isBreakfast: true };
  }

  // Fallback: room metadata lacks breakfast, but every bookable rate in this
  // room includes breakfast via rate-level amenities or meal plan.
  if (
    room.products.length > 0 &&
    room.products.every(
      (p) =>
        p.amenities?.some((a) => BREAKFAST_AMENITIES.has(a.amenity)) ||
        (p.mealPlan?.kind != null &&
          BREAKFAST_MEAL_PLAN_KINDS.has(p.mealPlan.kind))
    )
  ) {
    return { label: freeBreakfastLabel, isBreakfast: true };
  }

  return undefined;
}
